import { BundleOptions, IServiceEnv, ServiceOptions, ServiceType } from "@excitare/cli-types";
import { PubSubEventData, PubSubEvents } from "@excitare/pubsub";
import findWorkspaceRoot from "find-yarn-workspace-root";
import { existsSync, mkdirpSync, readFileSync, writeFileSync } from "fs-extra";
import { fromFileSync } from "hasha";
import path, { dirname, join, relative } from "path";
import rimraf from "rimraf";

import { pubSubServer } from "..";
import { ForkedProcess } from "../fork/process";
import { resolveSource } from "../select";

function replacer(_key: any, value: any) {
  if (value instanceof RegExp) {
    return "__REGEXP " + value.toString();
  } else {
    return value;
  }
}

/* start search fragment: buildDirDeletion */
function cleanAndCreate(dir: string) {
  if (!existsSync(dir)) {
    console.info("[build directory cleanup] Skipping cleaning of %s - does not exists", dir);
    return;
  }

  const hashFile = join(dir, "./.yarnhash");
  const workspaceHash = fromFileSync(join(findWorkspaceRoot(process.cwd())!, "yarn.lock"));
  let needsCleaning = true;
  if (existsSync(hashFile)) {
    const hash = readFileSync(hashFile, "utf-8");
    if (workspaceHash === hash) {
      needsCleaning = false;
    }
  }

  if (needsCleaning) {
    console.info("[build directory cleanup] Cleaning %s", dir);
    const attempts = 5;
    let run = 0;
    while (true) {
      if (run > 0) {
        console.info("[build directory cleanup]Retrying (attempt %s / %s)", run, attempts);
      }
      try {
        rimraf.sync(dir, {
          maxBusyTries: 5
        });
        break;
      } catch (err) {
        if (++run > attempts || !err.message.includes("directory not empty")) {
          throw err;
        }

        console.info("[build directory cleanup] Unknown failure on '%s'.", dir);
      }
    }
    console.info("[build directory cleanup] Successfully cleaned %s", dir);

    mkdirpSync(dir);
    writeFileSync(hashFile, workspaceHash);
  } else {
    console.info(
      "[build directory cleanup] Skipping cleaning of %s - yarn.lock did not change",
      dir
    );
  }
}
/* end search fragment: buildDirDeletion */

let currentInspectPort = 9229;
export class ServiceInstance {
  private devDirectory = path.resolve(path.join(path.dirname(this.entry), "./.excitare-dev/"));
  private outputDirectory = path.resolve(path.join(this.devDirectory, "./dist/"));
  private workingDirectory = path.resolve(path.dirname(this.entry));
  private outputName = `bundle.js`;
  private output = path.join(this.outputDirectory, this.outputName);

  private inspectPort = ++currentInspectPort;

  constructor(
    private name: string,
    private type: ServiceType,
    private externals: string[],
    private bundleOptions: BundleOptions,
    private iconOptions: ServiceOptions.Icon | null,
    private entry: string,
    private io: SocketIO.Server
  ) {}

  buildProcess: ForkedProcess | null = null;
  childProcess: ForkedProcess | null = null;

  stop() {
    if (this.childProcess != null) {
      this.childProcess.kill();
      this.childProcess = null;
    }
  }

  stopBuild() {
    if (this.buildProcess != null) {
      this.buildProcess.kill();
      this.buildProcess = null;
    }
  }

  watch(onStarted: () => void, onDone: (errors?: string[]) => void) {
    if (this.buildProcess != null) {
      this.buildProcess.kill();
      this.buildProcess = null;
    }

    cleanAndCreate(this.devDirectory);

    if (!["enabled", "disabled"].includes(process.env.EXCITARE_ASYNC_TRACES!)) {
      throw new Error("Expected EXCITARE_ASYNC_TRACES to be either 'enabled' or 'disabled'");
    }

    this.buildProcess = new ForkedProcess(
      "webpack:" + this.name,
      this.workingDirectory,
      resolveSource(path.join(__dirname, "./process")),
      [
        "-r",
        process.env.SHOULD_USE_TSNODE
          ? require.resolve("ts-node/register")
          : require.resolve("source-map-support/register"),
        process.env.EXCITARE_ASYNC_TRACES === "enabled" && "--async-stack-traces",
        "--no-deprecation"
      ].filter(Boolean) as string[],
      {
        NAME: this.name,
        ENTRY: this.entry,
        TYPE: this.type,
        OUTPUT_DIRECTORY: this.outputDirectory,
        terserDir: JSON.stringify(join(this.outputDirectory, "../.terser-dev")),
        cacheLoaderDir: JSON.stringify(join(this.outputDirectory, "../.cache-loader-dev")),
        OUTPUT_NAME: this.outputName,
        EXTERNALS: JSON.stringify(this.externals),
        BUNDLE_OPTIONS: JSON.stringify(this.bundleOptions, replacer),
        ICON_OPTIONS:
          this.iconOptions != null
            ? JSON.stringify(
                {
                  ...this.iconOptions,
                  inputDir: path.resolve(
                    path.join(path.dirname(this.entry), this.iconOptions.outDir)
                  ),
                  outDir: path.resolve(path.join(path.dirname(this.entry), "./dist/icons"))
                },
                replacer
              )
            : undefined,
        TS_NODE_PROJECT: path.join(__dirname, "../../tsconfig.json"),
        EXCITARE_ASYNC_TRACES: process.env.EXCITARE_ASYNC_TRACES
      },
      ["builds"],
      this.io
    );

    pubSubServer().on(PubSubEvents.BuildStatus, data => {
      if (data.name === this.name) {
        console.log("Webpack status reported for %s: build %s", data.name, data.type);
        if (data.type === "started") {
          onStarted();
          for (const file of data.changedFiles) {
            console.log(`[${this.name}] ${relative(dirname(this.entry), file)} changed`);
          }
          if (data.changedFiles.length === 0) {
            console.log(`[${this.name}] no files changed, but restarting anyways`);
          }
          if (this.childProcess != null) {
            this.childProcess.write(`\n\rbuilding...`);
            this.childProcess.write(`\n\rchanged files:`);
          }
        }
        if (data.type === "successful") {
          if (this.childProcess != null) {
            this.childProcess.write(`\n\rbuild successful...`);
          }
          onDone();
        }
        if (data.type === "failure") {
          if (this.childProcess != null) {
            this.childProcess.write(`\n\rbuild failure...`);
          }
          onDone(data.errors);
        }
      }
    });
  }

  start(
    secret: string | null,
    preferredPort: number | undefined,
    host: string | undefined,
    childData: PubSubEventData.ClientInfo[],
    env: IServiceEnv,
    inspect: boolean
  ) {
    if (this.childProcess != null) {
      this.stop();
    }
    console.log("Starting %s in %s", this.name, this.workingDirectory);

    const envData: NodeJS.ProcessEnv = {
      secret: secret != null ? secret : undefined,
      name: this.name,
      childData: JSON.stringify(childData),
      port: preferredPort != null ? preferredPort.toString() : undefined,
      host: host != null ? host : undefined,
      DEPLOYMENT_TYPE: "local",
      EXCITARE_PATH: path.join(__dirname, "../../"),
      envData: JSON.stringify(env)
    };
    if (envData.secret == null) {
      delete envData.secret;
    }
    if (envData.port == null) {
      delete envData.port;
    }
    if (envData.host == null) {
      delete envData.host;
    }

    if (!["enabled", "disabled"].includes(process.env.EXCITARE_ASYNC_TRACES!)) {
      throw new Error("Expected EXCITARE_ASYNC_TRACES to be either 'enabled' or 'disabled'");
    }

    this.childProcess = new ForkedProcess(
      this.name,
      this.workingDirectory,
      this.output,
      [
        "-r",
        process.env.SHOULD_USE_TSNODE
          ? require.resolve("ts-node/register")
          : require.resolve("source-map-support/register"),
        "--no-deprecation",
        process.env.EXCITARE_ASYNC_TRACES === "enabled" && "--async-stack-traces",
        "--trace-warnings",
        inspect && String("--inspect=127.0.0.1:" + this.inspectPort)
      ].filter(Boolean) as string[],
      {
        ...envData,
        EXCITARE_ASYNC_TRACES: process.env.EXCITARE_ASYNC_TRACES
      },
      ["services", "Service: " + this.name],
      this.io
    );

    return new Promise(resolve => {
      const listener = (data: PubSubEventData.ClientInfo) => {
        if (data.name === this.name) {
          pubSubServer().off(PubSubEvents.ClientInfo, listener);
          process.nextTick(() => resolve());
        }
      };
      pubSubServer().on(PubSubEvents.ClientInfo, listener);
    });
  }
}
